/**
 * OneSignal Notification Service
 * Handles all OneSignal push notification functionality
 * Production-ready implementation with error handling and logging
 */

let oneSignalInitialized = false;
let oneSignalInitPromise = null;

const ONESIGNAL_SDK_URL =
  "https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js";

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const withOneSignal = async (callback, timeoutMs = 10000) => {
  const start = Date.now();

  while (Date.now() - start < timeoutMs) {
    if (window.OneSignal && typeof window.OneSignal.init === "function") {
      return callback(window.OneSignal);
    }

    if (window.OneSignalDeferred?.push) {
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(
          () => reject(new Error("OneSignal SDK was not ready before timeout")),
          Math.max(timeoutMs - (Date.now() - start), 0)
        );

        window.OneSignalDeferred.push(async (OneSignal) => {
          try {
            clearTimeout(timeout);
            resolve(await callback(OneSignal));
          } catch (error) {
            clearTimeout(timeout);
            reject(error);
          }
        });
      });
    }

    await wait(100);
  }

  throw new Error("OneSignal SDK was not ready before timeout");
};

const normalizePermission = (permission) => {
  if (permission === true || permission === "granted") return "granted";
  if (permission === "denied") return "denied";
  if (typeof Notification !== "undefined") return Notification.permission;
  return permission === false ? "default" : "default";
};

const getPushSubscription = (OneSignal) =>
  OneSignal.User?.PushSubscription || OneSignal.User?.pushSubscription;

const getPushSubscriptionId = (OneSignal) => {
  const pushSubscription = getPushSubscription(OneSignal);
  return pushSubscription?.id || null;
};

const waitForPushSubscriptionId = async (OneSignal, timeoutMs = 15000) => {
  const pushSubscription = getPushSubscription(OneSignal);

  if (!pushSubscription) {
    return null;
  }

  const existingId = getPushSubscriptionId(OneSignal);
  if (existingId) {
    return existingId;
  }

  return new Promise((resolve) => {
    let settled = false;

    const finish = (subscriptionId) => {
      if (settled) return;
      settled = true;
      if (typeof pushSubscription.removeEventListener === "function") {
        pushSubscription.removeEventListener("change", changeListener);
      }
      clearInterval(pollInterval);
      clearTimeout(timeout);
      resolve(subscriptionId || null);
    };

    const changeListener = (event) => {
      const subscriptionId =
        event?.current?.id ||
        getPushSubscriptionId(OneSignal);

      if (subscriptionId) {
        console.log("[OneSignal] Push subscription changed:", event.current);
        finish(subscriptionId);
      }
    };

    if (typeof pushSubscription.addEventListener === "function") {
      pushSubscription.addEventListener("change", changeListener);
    }

    const pollInterval = setInterval(() => {
      const subscriptionId = getPushSubscriptionId(OneSignal);
      if (subscriptionId) {
        finish(subscriptionId);
      }
    }, 500);

    const timeout = setTimeout(() => finish(null), timeoutMs);
  });
};

const ensurePushSubscriptionId = async () => {
  return await withOneSignal(async (OneSignal) => {
    const pushSubscription = getPushSubscription(OneSignal);

    if (!pushSubscription) {
      console.warn("[OneSignal] PushSubscription API is not available");
      return null;
    }

    const currentId = getPushSubscriptionId(OneSignal);
    if (currentId) {
      return currentId;
    }

    if (typeof pushSubscription.optIn === "function") {
      await pushSubscription.optIn();
    }

    return await waitForPushSubscriptionId(OneSignal);
  }, 20000);
};

/**
 * Initialize OneSignal SDK
 * @param {string} appId - OneSignal App ID
 * @returns {Promise<boolean>} - True if initialization successful
 */
export const initializeOneSignal = async (appId) => {
  try {
    if (oneSignalInitialized) {
      console.warn("[OneSignal] SDK already initialized");
      return true;
    }

    if (oneSignalInitPromise) {
      return oneSignalInitPromise;
    }

    // Only initialize in production or on HTTPS
    if (
      window.location.protocol === "http:" &&
      window.location.hostname !== "localhost"
    ) {
      console.warn(
        "[OneSignal] Skipping initialization on HTTP in production"
      );
      return false;
    }

    window.OneSignalDeferred = window.OneSignalDeferred || [];

    oneSignalInitPromise = (async () => {
      if (!document.querySelector(`script[src="${ONESIGNAL_SDK_URL}"]`)) {
        const script = document.createElement("script");
        script.src = ONESIGNAL_SDK_URL;
        script.defer = true;
        document.head.appendChild(script);
      }

      await withOneSignal(async (OneSignal) => {
        await OneSignal.init({
          appId: appId,
          allowLocalhostAsSecureOrigin: true,
          notificationClickHandlerMatch: "origin",
          serviceWorkerPath: "/OneSignalSDKWorker.js",
          serviceWorkerUpdatePath: "/OneSignalSDKUpdaterWorker.js",
        });
      });

      oneSignalInitialized = true;
      console.log("[OneSignal] Initialization successful");
      return true;
    })();

    return await oneSignalInitPromise;
  } catch (error) {
    oneSignalInitPromise = null;
    console.error("[OneSignal] Initialization failed:", error);
    return false;
  }
};

/**
 * Request notification permission from user
 * @returns {Promise<string>} - Permission status: 'granted', 'denied', or 'default'
 */
export const requestNotificationPermission = async () => {
  try {
    if (!isPushSupported()) {
      console.warn("[OneSignal] Push notifications not supported");
      return "denied";
    }

    const permission = await withOneSignal(async (OneSignal) => {
      if (OneSignal.Slidedown?.promptPush) {
        await OneSignal.Slidedown.promptPush();
      } else {
        await OneSignal.Notifications.requestPermission();
      }

      const currentPermission =
        typeof OneSignal.Notifications.permission !== "undefined"
          ? OneSignal.Notifications.permission
          : await OneSignal.Notifications.getPermission?.();

      return normalizePermission(currentPermission);
    });

    console.log("[OneSignal] Permission status:", permission);
    return permission;
  } catch (error) {
    console.error("[OneSignal] Permission request failed:", error);
    return "denied";
  }
};

/**
 * Get current notification permission status
 * @returns {Promise<string>} - 'granted', 'denied', or 'default'
 */
export const getNotificationPermissionStatus = async () => {
  try {
    return await withOneSignal(async (OneSignal) => {
      const permission =
        typeof OneSignal.Notifications?.permission !== "undefined"
          ? OneSignal.Notifications.permission
          : await OneSignal.Notifications?.getPermission?.();

      return normalizePermission(permission);
    }, 3000);
  } catch (error) {
    console.error("[OneSignal] Failed to get permission status:", error);
    return "default";
  }
};

/**
 * Check if push notifications are supported in this browser
 * @returns {boolean}
 */
export const isPushSupported = () => {
  try {
    if (window.OneSignal?.Notifications?.isPushSupported) {
      return window.OneSignal.Notifications.isPushSupported();
    }

    return (
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window
    );
  } catch (error) {
    return false;
  }
};

/**
 * Login OneSignal user - map app user to OneSignal external_id
 * @param {string} appUserId - Application user ID
 * @param {object} attributes - Optional user attributes
 * @returns {Promise<boolean>}
 */
export const loginOneSignalUser = async (appUserId, attributes = {}) => {
  try {
    await withOneSignal(async (OneSignal) => {
      if (!OneSignal.login) {
        throw new Error("OneSignal login API is not available");
      }

      await OneSignal.login(appUserId);

      if (Object.keys(attributes).length > 0) {
        await OneSignal.User?.addTags?.(attributes);
      }
    });

    console.log("[OneSignal] User logged in:", appUserId);
    return true;
  } catch (error) {
    console.error("[OneSignal] User login failed:", error);
    return false;
  }
};

/**
 * Logout OneSignal user
 * @returns {Promise<boolean>}
 */
export const logoutOneSignalUser = async () => {
  try {
    await withOneSignal(async (OneSignal) => {
      await OneSignal.logout();
    });
    console.log("[OneSignal] User logged out");
    return true;
  } catch (error) {
    console.error("[OneSignal] User logout failed:", error);
    return false;
  }
};

/**
 * Get OneSignal subscription ID
 * @returns {Promise<string|null>} - Subscription ID or null
 */
export const getSubscriptionId = async () => {
  try {
    const subscriptionId = await withOneSignal(async (OneSignal) => {
      return getPushSubscriptionId(OneSignal);
    }, 3000);

    if (subscriptionId) {
      console.log("[OneSignal] Subscription ID:", subscriptionId);
    }
    return subscriptionId;
  } catch (error) {
    console.error("[OneSignal] Failed to get subscription ID:", error);
    return null;
  }
};

export const registerCurrentOneSignalDevice = async (user, apiBaseUrl, token) => {
  try {
    if (!user?.id || !apiBaseUrl || !token) {
      return false;
    }

    const externalId = user.id.toString();
    const loggedIn = await loginOneSignalUser(externalId, {
      username: user.name || user.UserName || "",
      role: user.role ? user.role.toString() : "",
    });

    if (!loggedIn) {
      return false;
    }

    let permission = await getNotificationPermissionStatus();
    if (permission === "default") {
      permission = await requestNotificationPermission();
    }

    if (permission !== "granted") {
      console.warn("[OneSignal] Permission not granted, device not subscribed");
      return false;
    }

    const subscriptionId = await ensurePushSubscriptionId();

    if (!subscriptionId) {
      console.warn("[OneSignal] Push permission granted but no subscription ID was created");
      return false;
    }

    const response = await fetch(`${apiBaseUrl}/api/notifications/devices/subscribe`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        userId: Number(user.id),
        oneSignalExternalId: externalId,
        subscriptionId,
        browser: navigator.userAgent,
        deviceType: /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)
          ? "mobile"
          : "web",
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("[OneSignal] Device registration failed:", errorText);
      return false;
    }

    console.log("[OneSignal] Device registered with backend");
    return true;
  } catch (error) {
    console.error("[OneSignal] Device registration failed:", error);
    return false;
  }
};

/**
 * Set up notification event listeners
 * @param {function} onNotificationReceived - Callback when notification received
 * @param {function} onNotificationClicked - Callback when notification clicked
 */
export const setupNotificationListeners = (
  onNotificationReceived,
  onNotificationClicked
) => {
  try {
    if (!window.OneSignal?.Notifications) {
      console.warn("[OneSignal] SDK not ready for event listeners");
      return;
    }

    // Listen for notifications when app is in focus
    if (onNotificationReceived) {
      window.OneSignal.Notifications.addEventListener(
        "foreground",
        onNotificationReceived
      );
      console.log("[OneSignal] Foreground notification listener registered");
    }

    // Listen for notification clicks
    if (onNotificationClicked) {
      window.OneSignal.Notifications.addEventListener(
        "click",
        onNotificationClicked
      );
      console.log("[OneSignal] Click listener registered");
    }
  } catch (error) {
    console.error("[OneSignal] Failed to setup event listeners:", error);
  }
};

/**
 * Handle notification click with deep linking
 * @param {object} notification - Notification event object
 * @param {function} navigationCallback - Callback to handle navigation
 */
export const handleNotificationClick = (notification, navigationCallback) => {
  try {
    const actionUrl = notification?.notification?.data?.url || "/";
    if (navigationCallback) {
      navigationCallback(actionUrl);
    }
  } catch (error) {
    console.error("[OneSignal] Failed to handle notification click:", error);
  }
};

/**
 * Enable or disable notifications for current user
 * @param {boolean} enabled - True to enable, false to disable
 * @returns {Promise<boolean>}
 */
export const setNotificationsEnabled = async (enabled) => {
  try {
    await withOneSignal(async (OneSignal) => {
      if (enabled) {
        await OneSignal.Notifications.requestPermission();
      } else {
        console.log("[OneSignal] To disable notifications, adjust browser settings");
      }
    });

    return true;
  } catch (error) {
    console.error("[OneSignal] Failed to set notification state:", error);
    return false;
  }
};

/**
 * Verify OneSignal is properly initialized
 * @returns {boolean}
 */
export const isOneSignalReady = () => {
  return (
    oneSignalInitialized &&
    (window.OneSignal || window.OneSignalDeferred)
  );
};

/**
 * Get service worker status
 * @returns {Promise<object>} - Service worker status info
 */
export const getServiceWorkerStatus = async () => {
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    return {
      registered: !!registration,
      ready: registration?.active ? true : false,
      scope: registration?.scope || null,
    };
  } catch (error) {
    console.error("[OneSignal] Failed to check service worker:", error);
    return { registered: false, ready: false, scope: null };
  }
};

export default {
  initializeOneSignal,
  requestNotificationPermission,
  getNotificationPermissionStatus,
  isPushSupported,
  loginOneSignalUser,
  logoutOneSignalUser,
  getSubscriptionId,
  registerCurrentOneSignalDevice,
  setupNotificationListeners,
  handleNotificationClick,
  setNotificationsEnabled,
  isOneSignalReady,
  getServiceWorkerStatus,
};
